/**
 * Image sizing per the ESS integration spec: max edge 1280px, JPEG ~0.7
 * (≈300–500 KB; AWS Rekognition hard-rejects images over 5 MB).
 */
export const MAX_EDGE_PX = 1280;
export const JPEG_QUALITY = 0.7;

export interface TargetSize {
  width: number;
  height: number;
}

/** Scale (down only) so the longest edge is at most `maxEdge`, preserving aspect. */
export function computeTargetSize(
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE_PX,
): TargetSize {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The source-frame region an `object-cover` preview actually shows: scaled to
 * fill the viewport, center-cropped. Degenerate viewports (jsdom, detached
 * elements) fall back to the full frame.
 */
export function computeCoverCrop(
  frame: { width: number; height: number },
  viewport: { width: number; height: number },
): CropRect {
  if (
    frame.width <= 0 ||
    frame.height <= 0 ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return { x: 0, y: 0, width: frame.width, height: frame.height };
  }
  const scale = Math.max(viewport.width / frame.width, viewport.height / frame.height);
  const width = Math.min(frame.width, viewport.width / scale);
  const height = Math.min(frame.height, viewport.height / scale);
  return {
    x: (frame.width - width) / 2,
    y: (frame.height - height) / 2,
    width,
    height,
  };
}

/**
 * Grab the current (un-mirrored) frame from the live preview, downscaled and
 * encoded as a base64 JPEG data URL — the exact string ESS forwards to
 * `verify-image` / `enroll`.
 *
 * WYSIWYG: the capture is cropped to the region the `object-cover` preview
 * actually displayed (the video element's client box), so the photo the user
 * reviews matches what they saw — nothing outside the preview leaks in.
 */
export function captureVideoFrame(video: HTMLVideoElement): string {
  const frame = { width: video.videoWidth, height: video.videoHeight };
  const crop = computeCoverCrop(frame, {
    width: video.clientWidth,
    height: video.clientHeight,
  });
  const { width, height } = computeTargetSize(crop.width, crop.height);
  if (width === 0 || height === 0) {
    throw new Error('Video stream has no frames to capture yet.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  ctx.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}
